import { Box, Button, IconButton, Menu, MenuItem, Stack, Typography } from "@mui/material";
import CustomAutoComplete from "global/components/CustomAutoComplete/CustomAutoComplete";
import { isTruthy } from "helpers/methods";
import { Reviewers } from "models/interfaces";
import React, { Dispatch, useEffect, useRef } from "react";
import {
  deleteReviewer,
  setContacts,
  updateIndividualReviewer,
} from "redux/appSlice";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";
import { getContactSuggestion } from "../SignRequestHelper";
import strings from "global/constants/StringConstants";
import { useDrag } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import deleteGreyIcon from "../../../assets/icons/deleteGreyIcon.svg";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { AnyAction, ThunkDispatch } from "@reduxjs/toolkit";
import {
  getParticipantNameError,
  isValidParticipantNameLength,
} from "screens/Shared/CommonHelper";
import approversAndSignersStyles from "../ApproversAndSigners.styles";
import recipientsTokens from "../ApproversAndSigners.tokens";
import { CloseGlyph, PlusGlyph } from "./RecipientsIcons";

const ItemType = {
  APPROVER: "approver",
};

const styles = approversAndSignersStyles;

const GroupReviewerRow = ({
  reviewer,
  groupIndex,
  children,
  isMobile,
  dispatch,
  handleDelete,
}: {
  reviewer: any;
  groupIndex?: number;
  children: React.ReactNode;
  isMobile?: any;
  dispatch: ThunkDispatch<any, undefined, AnyAction> & Dispatch<AnyAction>;
  handleDelete?: any;
}) => {
  const [mobileAnchorEl, setMobileAnchorEl] =
    React.useState<null | HTMLElement>(null);
  const mobileOpen = Boolean(mobileAnchorEl);

  const handleMobileClose = () => setMobileAnchorEl(null);

  const [{ isDragging }, drag, dragPreview] = useDrag(() => ({
    type: ItemType.APPROVER,
    item: {
      dragType: "GROUP_MEMBER",
      signerId: reviewer.id,
      fromGroupOrder: reviewer.order,
      blockIndex: groupIndex,
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  useEffect(() => {
    if (!isMobile) return;
    dragPreview(getEmptyImage(), { captureDraggingState: true });
  }, [dragPreview, isMobile]);

  return (
    <Box sx={{ ...(styles.memberRow as object), opacity: isDragging ? 0 : 1 }}>
      {/* The design's bullet doubles as the grab handle for moving a member out. */}
      <Box
        ref={drag}
        title="Drag to move out of the group"
        sx={{ ...(styles.memberBullet as object), cursor: "grab" }}
      />

      {children}

      {isMobile ? (
        <IconButton
          size="small"
          aria-label="Member options"
          aria-haspopup="true"
          aria-controls={
            mobileOpen ? `member-options-menu-${reviewer.id}` : undefined
          }
          aria-expanded={mobileOpen}
          onClick={(e) => setMobileAnchorEl?.(e.currentTarget)}
        >
          <MoreVertIcon />
        </IconButton>
      ) : (
        <IconButton
          disableRipple
          aria-label="Remove member"
          onClick={() => {
            dispatch(deleteReviewer({ reviewerId: reviewer.id }));
            handleDelete?.(reviewer.id, reviewer.email);
          }}
          sx={styles.memberRemove}
        >
          <CloseGlyph sx={styles.memberRemoveIcon} />
        </IconButton>
      )}
      <Menu
          id={`member-options-menu-${reviewer.id}`}
          anchorEl={mobileAnchorEl}
          open={mobileOpen}
          onClose={handleMobileClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          MenuListProps={{
            sx: { py: isMobile ? 0 : undefined },
          }}
        >
          <MenuItem
            onClick={() => {
              dispatch(deleteReviewer({ reviewerId: reviewer.id }));
              handleDelete?.(reviewer.id, reviewer.email);
            }}
            sx={{ fontSize: "0.875rem", padding: "0.5rem 1rem" }}
          >
            <Box sx={{ mr: 1, display: "flex", alignItems: "center" }}>
              <Box
                component="img"
                src={deleteGreyIcon}
                alt="Delete icon"
                aria-hidden="true"
                sx={{ width: "1.25rem" }}
              />
            </Box>
            <Typography variant="subtitle2">Delete</Typography>
          </MenuItem>
        </Menu>
    </Box>
  );
};

const ApprovalGroupFields = (props: {
  eachApprovers: Reviewers;
  contactList: any;
  setContactList: Function;
  handleRemoveContacts: Function;
  handleResetContacts: Function;
  handleAdd?: Function;
  index?: number;
  selectedIndex?: number;
  group?: any;
  excludeIds: string[];
  setExcludeIds: Function;
  handleDelete: any;
  isMobile?: any;
}) => {
  const dispatch = useAppDispatch();
  let { errors, contacts } = useAppSelector((state: RootState) => state.app);

  const timeoutRef: any = useRef(null);
  const timeoutRef2: any = useRef(null);
  const commonFieldsRef = useRef<HTMLDivElement | null>(null);

  const getCommonFields = (item: any, index: number) => {
    return (
      <Box
        ref={commonFieldsRef}
        key={item.id}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: recipientsTokens.approvers.memberGap,
          flex: 1,
          minWidth: 0,
        }}
      >
        <Box sx={styles.memberField(true)}>
          <CustomAutoComplete
            id={`signer-${item.id}-name`}
            ariaLabel={`Approver group member ${index + 1} name`}
            placeHolder="Member name"
            name="name"
            freeSolo={true}
            value={item.name || ""}
            onChange={(e: any, newValue: any, reason: any) => {
              if (newValue && typeof newValue === "object") {
                props.setExcludeIds([
                  ...props.excludeIds.filter((id) => id !== item.email),
                  newValue.email,
                ]);

                dispatch(
                  updateIndividualReviewer({
                    reviewerId: item.id,
                    fieldsToUpdate: {
                      name: newValue.name.substring(0, 200) || "",
                      email:
                        newValue.email.substring(0, 256) ||
                        item.email.substring(0, 256),
                    },
                  }),
                );
              } else if (typeof newValue === "string") {
                const trimmedValue = newValue
                  .trimStart()
                  .replace(/(?:^\s+|\s+$|\t+)/g, " ");

                dispatch(
                  updateIndividualReviewer({
                    reviewerId: item.id,
                    fieldsToUpdate: {
                      name: trimmedValue,
                    },
                  }),
                );
              } else if (newValue === null || newValue === "") {
                dispatch(
                  updateIndividualReviewer({
                    reviewerId: item.id,
                    fieldsToUpdate: {
                      name: "",
                    },
                  }),
                );
              }
            }}
            onBlur={props.handleResetContacts}
            onChangeForTextField={(
              e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
            ) => {
              const { value } = e.target;
              const trimmedValue = value
                .trimStart()
                .replace(/(?:^\s+|\s+$|\t+)/g, " ")
                .substring(0, 200);

              dispatch(
                updateIndividualReviewer({
                  reviewerId: item.id,
                  fieldsToUpdate: {
                    name: trimmedValue,
                  },
                }),
              );

              clearTimeout(timeoutRef.current);

              timeoutRef.current = setTimeout(async () => {
                if (isTruthy(value.trim())) {
                  dispatch(setContacts([]));
                  await getContactSuggestion(
                    value.trim(),
                    dispatch,
                    props.excludeIds,
                    props.setContactList,
                    props.index,
                    "name",
                  );
                } else {
                  await getContactSuggestion(
                    "",
                    dispatch,
                    props.excludeIds,
                    props.setContactList,
                    props.index,
                    "name",
                  );
                  dispatch(setContacts(contacts));
                }
              }, 500);
            }}
            filterOptions={(options: any, { inputValue }: any) =>
              options.filter(
                (option: any) =>
                  option.name
                    .toLowerCase()
                    .includes(inputValue.toLowerCase()) ||
                  option.email.toLowerCase().includes(inputValue.toLowerCase()),
              )
            }
            renderOption={(prop: any, option: any) => (
              <li {...prop}>
                <Stack direction="column">
                  <Typography variant="subtitle1" fontWeight={"bold"}>
                    {option.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "grey" }}>
                    {option.email}
                  </Typography>
                </Stack>
              </li>
            )}
            ref={timeoutRef}
            options={(props.contactList.name &&
            props.contactList.name[props.index!]
              ? props.contactList.name[props.index!]
              : contacts || []
            )?.filter(
              (c: any) =>
                !props.excludeIds
                  .map((id: string) => id.toLowerCase())
                  .includes(c.email.toLowerCase()),
            )}
            getOptionLabel={(option: any) => {
              if (typeof option === "string") return option;
              return option.name || "";
            }}
            error={
              !(
                isTruthy(item.name) && isValidParticipantNameLength(item.name)
              ) && errors.name
            }
            errorMessage={
              !isTruthy(item.name) && errors.name
                ? "Name is required"
                : getParticipantNameError(item.name) || ""
            }
            PaperComponent={(paperProps: any) => (
              <Stack
                {...paperProps}
                width={"100%"}
                mt={2}
                mb={2}
                bgcolor={"white"}
                boxShadow="0px 4px 6px -1px rgb(0 0 0 / 0.1)"
                sx={{
                  ...(props.isMobile && {
                    "& .MuiAutocomplete-listbox": {
                      overflowX: "auto",
                      overflowY: "auto",
                      scrollbarWidth: "thin",
                    },
                    "& .MuiAutocomplete-option": {
                      whiteSpace: "nowrap",
                      minWidth: "max-content",
                    },
                  }),
                }}
              />
            )}
          />
        </Box>
        <Box sx={styles.memberField(false)}>
          <CustomAutoComplete
            id={`signer-${item.id}-email`}
            ariaLabel={`Approver group member ${index + 1} email`}
            placeHolder="Member email"
            name="email"
            freeSolo={true}
            value={item.email || ""}
            onChange={(e: any, newValue: any, reason: any) => {
              if (newValue && typeof newValue === "object") {
                props.setExcludeIds([
                  ...props.excludeIds.filter((id) => id !== item.email),
                  newValue.email,
                ]);

                dispatch(
                  updateIndividualReviewer({
                    reviewerId: item.id,
                    fieldsToUpdate: {
                      name: newValue.name || item.name,
                      email: newValue.email || "",
                    },
                  }),
                );
              } else if (typeof newValue === "string") {
                dispatch(
                  updateIndividualReviewer({
                    reviewerId: item.id,
                    fieldsToUpdate: {
                      email: newValue.replace(/\s/g, "").substring(0, 256),
                    },
                  }),
                );
              } else if (newValue === null || newValue === "") {
                dispatch(
                  updateIndividualReviewer({
                    reviewerId: item.id,
                    fieldsToUpdate: {
                      email: "",
                    },
                  }),
                );
              }
            }}
            onBlur={props.handleResetContacts}
            onChangeForTextField={(
              e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
            ) => {
              const { value } = e.target;
              dispatch(
                updateIndividualReviewer({
                  reviewerId: item.id,
                  fieldsToUpdate: {
                    email: value.replace(/\s/g, "").substring(0, 256),
                  },
                }),
              );

              clearTimeout(timeoutRef2.current);

              timeoutRef2.current = setTimeout(() => {
                if (isTruthy(value.trim())) {
                  getContactSuggestion(
                    value.trim(),
                    dispatch,
                    props.excludeIds,
                    props.setContactList,
                    props.index,
                    "email",
                  );
                } else {
                  dispatch(setContacts([]));
                }
              }, 500);
            }}
            filterOptions={(options: any, { inputValue }: any) =>
              options.filter(
                (option: any) =>
                  option.email
                    .toLowerCase()
                    .includes(inputValue.toLowerCase()) ||
                  option.name.toLowerCase().includes(inputValue.toLowerCase()),
              )
            }
            renderOption={(prop: any, option: any) => (
              <li {...prop}>
                <Stack direction="column">
                  <Typography variant="subtitle1" fontWeight={"bold"}>
                    {option.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "grey" }}>
                    {option.email}
                  </Typography>
                </Stack>
              </li>
            )}
            ref={timeoutRef2}
            options={(props.contactList.email &&
            props.contactList.email[props.index!]
              ? props.contactList.email[props.index!]
              : contacts || []
            )?.filter(
              (c: any) =>
                !props.excludeIds
                  .map((id: string) => id.toLowerCase())
                  .includes(c.email.toLowerCase()),
            )}
            getOptionLabel={(option: any) => {
              if (typeof option === "string") return option;
              return option.email || "";
            }}
            error={
              (!isTruthy(item.email) || !strings.regex.test(item.email)) &&
              errors.email
            }
            PaperComponent={(paperProps: any) => (
              <Stack
                {...paperProps}
                width={"100%"}
                mt={2}
                mb={2}
                bgcolor={"white"}
                boxShadow="0px 4px 6px -1px rgb(0 0 0 / 0.1)"
                sx={{
                  ...(props.isMobile && {
                    "& .MuiAutocomplete-listbox": {
                      overflowX: "auto",
                      overflowY: "auto",
                      scrollbarWidth: "thin",
                    },
                    "& .MuiAutocomplete-option": {
                      whiteSpace: "nowrap",
                      minWidth: "max-content",
                    },
                  }),
                }}
              />
            )}
          />
        </Box>
      </Box>
    );
  };

  const getTextFields = () => {
    return (
      <Box sx={styles.membersBlock}>
        {props.group.map((item: any, index: number) => (
          <GroupReviewerRow
            key={item.id}
            reviewer={item}
            groupIndex={index}
            isMobile={props.isMobile}
            dispatch={dispatch}
            handleDelete={props.handleDelete}
          >
            {getCommonFields(item, index)}
          </GroupReviewerRow>
        ))}
        <Button
          disableRipple
          onClick={() => {
            getContactSuggestion("", dispatch, props.excludeIds);
            props.handleAdd!(true, props.eachApprovers.group);
            if (props.contactList.length < 10) {
              getContactSuggestion("", dispatch);
            }
          }}
          sx={styles.addMember}
        >
          <PlusGlyph sx={styles.addMemberIcon} />
          Add member
        </Button>
      </Box>
    );
  };

  return getTextFields();
};

export default ApprovalGroupFields;
